import { Schema } from '@tiptap/pm/model';
import { ChangeSet } from '@tiptap/pm/changeset';
import { StepMap, Transform } from '@tiptap/pm/transform';

// Compare the complete source, including field markers and column widths.
const schema = new Schema({ nodes: { doc: { content: 'text*' }, text: {} } });
const document = (source: string) => schema.node('doc', null, source ? schema.text(source) : undefined);
const changes = (base: string, next: string) => ChangeSet.create(document(base))
    .addSteps(document(next), [new StepMap([0, base.length, next.length])], null).changes;

/** Reapply only the local edits; ambiguous overlapping edits still need a choice. */
export function mergeSummaryDraft(base: string, local: string, server: string): string | null {
    if (local === server || local === base) return server;
    if (server === base) return local;
    const ours = changes(base, local), theirs = changes(base, server);
    const mapping = new StepMap(theirs.flatMap(change => [change.fromA, change.toA - change.fromA, change.toB - change.fromB]));
    const merged = new Transform(document(server));
    for (const change of [...ours].reverse()) {
        const replacement = local.slice(change.fromB, change.toB);
        if (theirs.some(other => change.fromA === other.fromA && change.toA === other.toA &&
            replacement === server.slice(other.fromB, other.toB))) continue;
        const overlap = theirs.some(other => {
            if (change.fromA === change.toA && other.fromA === other.toA) return change.fromA === other.fromA;
            if (change.fromA === change.toA) return change.fromA > other.fromA && change.fromA < other.toA;
            if (other.fromA === other.toA) return other.fromA > change.fromA && other.fromA < change.toA;
            return change.fromA < other.toA && change.toA > other.fromA;
        });
        if (overlap) return null;
        const from = mapping.map(change.fromA, 1);
        const to = change.fromA === change.toA ? from : mapping.map(change.toA, -1);
        if (from > to) return null;
        merged.replaceWith(from, to, replacement ? schema.text(replacement) : []);
    }
    return merged.doc.textContent;
}
